import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../App';
import { useNotifications } from '../contexts/NotificationContext';
import axios from 'axios';
import { formatPlaceName } from '../utils/placeName';
import BottomNavigation from '../components/BottomNavigation';
import LanguageToggle from '../components/LanguageToggle';
import { LogOut, Heart, MapPin, Trash2, Bell, Search, X } from 'lucide-react';
import { getToken } from '../utils/auth';
import useFollowingBadge from '../hooks/useFollowingBadge';

const API = process.env.REACT_APP_BACKEND_URL;

const Favorites = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const notifications = useNotifications();
  const { markSeen: markFollowingSeen } = useFollowingBadge();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  // Iter 195 — Client-side search across the already-loaded favourites list.
  // Mirrors the Discover / Events / Offers UX. No backend change.
  const [searchTerm, setSearchTerm] = useState('');

  // Anonymous flow: pull the businessIds the device has favourited (from the
  // existing /api/notifications/preferences cache) and hydrate them into
  // full business objects so the card grid looks identical to the logged-in
  // version. No new backend endpoint.
  const fetchAnonymousFavorites = useCallback(async (ids) => {
    if (!ids || ids.length === 0) { setFavorites([]); setLoading(false); return; }
    try {
      setLoading(true);
      // /api/businesses returns approved-only entries; we filter to ours.
      const r = await axios.get(`${API}/api/businesses`);
      const idSet = new Set(ids);
      setFavorites((r.data || []).filter(b => idSet.has(b.id)));
    } catch (err) {
      console.error('Error hydrating anonymous favourites:', err);
      setFavorites([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchFavorites = useCallback(async () => {
    if (user) {
      const token = getToken();
      if (!token) { setLoading(false); return; }
      try {
        setLoading(true);
        const res = await axios.get(`${API}/api/user/favorites`, { headers: { Authorization: `Bearer ${token}` } });
        setFavorites(res.data || []);
      } catch (err) {
        console.error('Error fetching favorites:', err);
      } finally {
        setLoading(false);
      }
    } else {
      await fetchAnonymousFavorites(notifications?.preferences?.favouriteBusinessIds || []);
    }
  }, [user, notifications?.preferences?.favouriteBusinessIds, fetchAnonymousFavorites]);

  useEffect(() => { fetchFavorites(); }, [fetchFavorites]);

  // Mark "Following" as seen on mount — clears the bottom-nav red dot.
  useEffect(() => { markFollowingSeen(); }, [markFollowingSeen]);

  const removeFavorite = async (businessId) => {
    if (user) {
      const token = getToken();
      if (!token) return;
      try {
        await axios.delete(`${API}/api/user/favorites/${businessId}`, { headers: { Authorization: `Bearer ${token}` } });
        setFavorites(prev => prev.filter(b => b.id !== businessId));
      } catch (err) {
        console.error('Error removing favorite:', err);
      }
      return;
    }
    // Anonymous: flip the favourite on the device subscription
    try {
      await notifications.toggleFavourite(businessId);
      setFavorites(prev => prev.filter(b => b.id !== businessId));
    } catch (err) {
      console.error('Error removing anonymous favorite:', err);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Iter 195 — Filter the saved favourites on the fly. Searches name + city +
  // category so users can quickly find a specific saved listing.
  const normalisedSearch = searchTerm.trim().toLowerCase();
  const visibleFavorites = normalisedSearch
    ? favorites.filter((b) => {
        const haystack = [b.businessName, b.city, b.category]
          .filter(Boolean).join(' ').toLowerCase();
        return haystack.includes(normalisedSearch);
      })
    : favorites;

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <header className="bg-gradient-to-r from-purple-600 to-purple-700 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold flex items-center gap-2" data-testid="favorites-title">
              <Heart className="h-6 w-6" />
              {t('nav.favorites', 'Favorites')}
            </h1>
            <div className="flex items-center gap-3">
              <LanguageToggle />
              {user && (
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
                  data-testid="logout-button"
                >
                  <LogOut className="h-4 w-4" />
                  <span className="hidden sm:inline">{t('logout', 'Logout')}</span>
                </button>
              )}
            </div>
          </div>

          {/* Iter 195 — Search row, same modern pattern as Discover / Events /
              Offers. Only mounted when there's something to search through —
              an empty favourites list shows the onboarding CTA below instead. */}
          {favorites.length > 0 && (
            <div className="mt-3 relative">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-purple-400"
                aria-hidden="true"
              />
              <input
                type="search"
                inputMode="search"
                enterKeyHint="search"
                placeholder={t('searchFavoritesPlaceholder', 'Search your favourites...')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
                className="w-full pl-10 pr-10 py-2 rounded-lg text-gray-900 focus:ring-2 focus:ring-white outline-none"
                data-testid="favorites-search-input"
                aria-label={t('searchFavoritesPlaceholder', 'Search your favourites...')}
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                  data-testid="favorites-search-clear-btn"
                  aria-label={t('clearSearch', 'Clear search')}
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          )}
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {loading ? (
          <div className="text-center py-12 text-gray-500">{t('loading', 'Loading...')}</div>
        ) : favorites.length === 0 ? (
          <div className="space-y-4">
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <Heart className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('noFavoritesYet', 'Not following any businesses yet')}</h2>
              <p className="text-gray-500 mb-6">
                {t('startExploring', 'Start exploring businesses and follow your favourites here.')}
              </p>
              <button
                onClick={() => navigate('/discover')}
                className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                data-testid="discover-businesses-btn"
              >
                {t('discoverBusinesses', 'Discover Businesses')}
              </button>
            </div>

            {favorites.length === 0 && (
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-xl p-6 text-center" data-testid="follow-empty-state">
                <Bell className="h-10 w-10 text-purple-400 mx-auto mb-3" />
                <h3 className="text-base font-semibold text-gray-800 mb-1">{t('followEmptyTitle')}</h3>
                <p className="text-sm text-gray-500 mb-4">{t('followEmptyDesc')}</p>
                <button
                  onClick={() => navigate('/discover')}
                  className="px-5 py-2.5 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors"
                  data-testid="follow-empty-cta"
                >
                  {t('followEmptyCta')}
                </button>
              </div>
            )}
          </div>
        ) : (
          <>
            {visibleFavorites.length === 0 ? (
              <div className="bg-white rounded-xl shadow-sm p-8 text-center" data-testid="favorites-no-search-results">
                <Heart className="h-14 w-14 text-gray-300 mx-auto mb-3" />
                <h2 className="text-lg font-semibold text-gray-900 mb-1">{t('noFavoritesForSearch', 'No favourites match your search')}</h2>
                <p className="text-sm text-gray-500 mb-5">&ldquo;{searchTerm}&rdquo;</p>
                <button
                  onClick={() => setSearchTerm('')}
                  className="px-5 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium"
                  data-testid="favorites-clear-search-cta"
                >
                  {t('clearSearch', 'Clear search')}
                </button>
              </div>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {visibleFavorites.map((biz) => (
              <div key={biz.id} className="bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-md transition-shadow" data-testid={`favorite-card-${biz.id}`}>
                <div
                  className="cursor-pointer"
                  onClick={() => navigate(`/business/${biz.id}`)}
                >
                  {biz.photos?.[0] ? (
                    <img src={biz.photos[0]} alt={biz.businessName} className="w-full aspect-[16/10] object-cover" style={{ objectPosition: 'center 30%' }} />
                  ) : (
                    <div className="w-full aspect-[16/10] bg-gradient-to-br from-purple-100 to-purple-200 flex items-center justify-center">
                      <Heart className="h-10 w-10 text-purple-300" />
                    </div>
                  )}
                  <div className="p-4">
                    <h3 className="font-semibold text-gray-900 truncate">{biz.businessName}</h3>
                    <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                      <MapPin className="h-3.5 w-3.5" />
                      {formatPlaceName(biz.city)}
                    </p>
                    <p className="text-xs text-purple-600 mt-1">{biz.category}</p>
                  </div>
                </div>
                <div className="px-4 pb-3">
                  <button
                    onClick={() => removeFavorite(biz.id)}
                    className="w-full flex items-center justify-center gap-2 py-2 text-sm text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
                    data-testid={`remove-favorite-${biz.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                    {t('removeFavorite', 'Remove')}
                  </button>
                </div>
              </div>
            ))}
              </div>
            )}
          </>
        )}
      </div>

      <BottomNavigation />
    </div>
  );
};

export default Favorites;
